import React, { useMemo, useRef } from "react";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE, type StepperTabsHandle } from "@components/StepperTabs/StepperTabs.types";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { getFalSteps } from "../../utils";

const { imoFAL } = paths.vesselCall;

const ImoFAL: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestNavigation } = useNavigationGuard();
  const location = useLocation();
  const stepperTabsRef = useRef<StepperTabsHandle>(null);
  const outletCtx = useOutletContext<VesselCallOutletContext>();
  const steps = useMemo(() => getFalSteps(), []);

  const navigatePreserve = (to: string) => {
    navigate({ pathname: to, search: location.search }, { state: location.state });
  };

  const base = useMemo(() => {
    const idx = location.pathname.indexOf(imoFAL);
    return idx >= 0 ? location.pathname.slice(0, idx + imoFAL.length) : location.pathname;
  }, [location.pathname]);

  const toAbs = (leaf?: string) => (leaf ? `${base}/${leaf}` : base);

  const activeIndex = useMemo(() => {
    const p = location.pathname;
    const i = steps.findIndex((s) => p.endsWith(`/${s.path}`));
    return i === -1 ? 0 : i;
  }, [location.pathname, steps]);

  return (
    <div className="pb-8 w-[100%] mx-0 max-w-[100%] overflow-x-auto flex flex-col box-border">
      <StepperTabs
        ref={stepperTabsRef}
        testId="imo-fal-stepper-tabs"
        currentStepIndex={activeIndex}
        stepperType={STEPPER_TYPE.HORIZONTAL}
        isTypeChangeable={false}
        onStepperTabChange={(index) => {
          if (index !== activeIndex) {
            return requestNavigation(() => {
              navigatePreserve(toAbs(steps[index].path));
              window.scrollTo({ top: 0, behavior: "smooth" });
            });
          }
        }}
      >
        {steps.map((s) => (
          <StepperTabPanel key={s.path} label={t(`${s.label}`)}>
            <div className="min-w-full flex flex-col bg-white">
              <Outlet context={outletCtx} />
            </div>
          </StepperTabPanel>
        ))}
      </StepperTabs>
    </div>
  );
};

export default ImoFAL;
